import { Card, CardProps, createStyles, makeStyles, Theme } from '@material-ui/core';
import classNames from 'classnames';
import React, { useCallback } from 'react';

import {
  FeedPostDetailsFragment,
  ReactionType,
  useAnalyticsPostImpressionMutation,
} from '../../../../graphql/gen/types';
import { bottomBorderStyle, spacings } from '../../../../theme';
import { PlexHeader } from '../../details/plex/PlexHeader';
import { useEffectOnce } from '../../details/useEffectOnce';
import { logAnalytics } from '../../shared/logAnalytics';
import { useVisibleDelay } from '../useVisibleDelay';
import { PlexPostContent } from './PlexPostContent';
import { PlexPostFooter } from './PlexPostFooter';

type Props = {
  plexPost: FeedPostDetailsFragment;
  onSelect: (id: string) => void;
  selected: boolean;
  visible?: boolean;
  id?: string;
};

export const PlexPost = React.memo(
  (props: Props): React.ReactElement => {
    const { plexPost, onSelect, selected, visible, id } = props;
    const classes = useStyles();
    const cardProps: Partial<CardProps> = selected ? { elevation: 0 } : {};
    const [analyticsPostImpression] = useAnalyticsPostImpressionMutation({ variables: { postId: plexPost.id } });
    useEffectOnce(() => {
      if (!plexPost.currentUserReactions.includes(ReactionType.AnalyticsImpression)) {
        analyticsPostImpression();
      }
    });

    const onVisible = useCallback((): void => {
      logAnalytics({ name: 'post viewed', details: { plexId: plexPost.plex.id, itemId: id } });
    }, [id, plexPost.plex.id]);
    useVisibleDelay({ visible, onVisible });

    return (
      <Card square id={id} className={classNames(visible && classes.rootVisible, classes.root)} {...cardProps}>
        <PlexHeader
          className={classes.header}
          plexName={plexPost.plex.name}
          plexColor={plexPost.plex.color}
          plexPublishStatus={plexPost.publishStatus}
          publishedDate={plexPost.publishedDate}
          authorName={plexPost.author.name}
          authorAvatar={plexPost.author.picture}
          authorTitle={plexPost.author.jobTitle}
          showUnreadBadge={!plexPost.notification?.isRead}
        />
        <PlexPostContent plexPost={plexPost} onSelect={onSelect} />
        <PlexPostFooter plexPost={plexPost} />
      </Card>
    );
  }
);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexShrink: 0,
      flexDirection: 'column',
      marginBottom: spacings.normal,
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1), 0px 0px 2px rgba(0, 0, 0, 0.06)',
      borderRadius: 6,
      backgroundColor: '#fff',
    },
    rootVisible: {
      // backgroundColor: '#e8e8e8', // uncomment to see which items are considered visible in viewport
    },
    header: {
      display: 'flex',
      flexDirection: 'row',
      paddingRight: spacings.xlarge,
      ...bottomBorderStyle,
    },
  })
);
